// src/components/fix-actions/quick-win-briefing.tsx
import { Lightbulb, Sparkles } from "lucide-react";
import type { FixAction } from "../../services/fix-actions/fix-actions-service";
import { getEffortColors, getImpactColors } from "./fix-actions-utils";
import { Button } from "../kit";

interface QuickWinBriefingProps {
  quickWinAction: FixAction;
  isBriefingMinimized: boolean;
  setIsBriefingMinimized: (minimized: boolean) => void;
  onStartAction: (actionId: number) => void;
  onGenerateAsset: (
    actionId: number,
    actionTitle: string,
    actionTheme: string,
    artifactTitle: string,
    artifactType: string,
    artifactDescription: string
  ) => void;
}

export function QuickWinBriefing({
  quickWinAction,
  isBriefingMinimized,
  setIsBriefingMinimized,
  onStartAction,
  onGenerateAsset,
}: QuickWinBriefingProps) {
  if (!quickWinAction) return null;

  return (
    <>
      {!isBriefingMinimized ? (
        <div
          className="px-4 md:px-8 py-4 md:py-6 border-b"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--muted)",
          }}
        >
          <div className="flex items-start gap-3 md:gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb
                  className="w-4 h-4 shrink-0"
                  style={{ color: "var(--alpha-teal)" }}
                />
                <h3
                  style={{
                    fontSize: "var(--text-base)",
                    fontWeight: "var(--font-weight-semibold)",
                    color: "var(--alpha-teal)",
                  }}
                >
                  Recommended Quick Win
                </h3>
              </div>
              <h4
                style={{
                  fontSize: "var(--text-base)",
                  fontWeight: "var(--font-weight-semibold)",
                  marginBottom: "8px",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {quickWinAction.title}
              </h4>
              <p
                style={{
                  color: "var(--muted-foreground)",
                  fontSize: "var(--text-sm)",
                  marginBottom: "16px",
                }}
              >
                {quickWinAction.description} This is your highest-impact action
                with low effort—ideal for building momentum and demonstrating
                quick value.
              </p>

              {/* Bottom row — stacks on mobile so button is full-width */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      style={{
                        fontSize: "var(--text-sm)",
                        color: "var(--muted-foreground)",
                      }}
                    >
                      Effort:
                    </span>
                    <span
                      style={{
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-weight-semibold)",
                        color: getEffortColors(quickWinAction.effort).text,
                      }}
                    >
                      {quickWinAction.effort}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      style={{
                        fontSize: "var(--text-sm)",
                        color: "var(--muted-foreground)",
                      }}
                    >
                      Impact:
                    </span>
                    <span
                      style={{
                        fontSize: "var(--text-sm)",
                        fontWeight: "var(--font-weight-semibold)",
                        color: getImpactColors(quickWinAction.impactLevel).text,
                      }}
                    >
                      {quickWinAction.impactLevel}
                    </span>
                  </div>
                  {quickWinAction.strategicTheme && (
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        style={{
                          fontSize: "var(--text-sm)",
                          color: "var(--muted-foreground)",
                        }}
                      >
                        Theme:
                      </span>
                      <span
                        style={{
                          fontSize: "var(--text-sm)",
                          fontWeight: "var(--font-weight-medium)",
                        }}
                      >
                        {quickWinAction.strategicTheme}
                      </span>
                    </div>
                  )}
                </div>

                <Button
                  onClick={() => {
                    const firstArtifact = quickWinAction.artifacts?.[0];
                    if (firstArtifact) {
                      onGenerateAsset(
                        quickWinAction.id,
                        quickWinAction.title,
                        quickWinAction.strategicTheme || "",
                        firstArtifact.title,
                        firstArtifact.type,
                        firstArtifact.description
                      );
                    } else {
                      onStartAction(quickWinAction.id);
                    }
                  }}
                  className="w-full sm:w-auto"
                  icon={Sparkles}
                >Generate Asset
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="px-4 md:px-8 py-3 border-b cursor-pointer hover:bg-muted/50 transition-colors"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--background)",
          }}
          onClick={() => setIsBriefingMinimized(false)}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Lightbulb
                className="w-4 h-4 shrink-0"
                style={{ color: "var(--alpha-teal)" }}
              />
              <div className="min-w-0 flex-1">
                <span
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--alpha-teal)",
                    fontWeight: "var(--font-weight-semibold)",
                    marginRight: "8px",
                  }}
                >
                  Quick Win:
                </span>
                <span
                  className="truncate inline-block align-bottom max-w-full"
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--foreground)",
                    fontWeight: "var(--font-weight-medium)",
                  }}
                  title={`${quickWinAction.title} • ${quickWinAction.effort} effort • ${quickWinAction.impactLevel} impact`}
                >
                  {quickWinAction.title} • {quickWinAction.effort} effort •{" "}
                  {quickWinAction.impactLevel} impact
                </span>
              </div>
            </div>
            <Button variant="ghost" className="shrink-0">
              Expand
            </Button>
          </div>
        </div>
      )}
    </>
  );
}